import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import Pusher, { Channel } from 'pusher-js';
import { AuthService } from '../auth.service';
import { CustomToastService } from '../custom-toast.service';

export type TurnstileEventType =
  | 'turnstile.connected'
  | 'turnstile.disconnected'
  | 'turnstile.scanned';

export interface TurnstileData {
  id?: number;
  name?: string;
  mac_address?: string;
  is_online?: boolean;
  location?: string;
  last_scan_at?: string;
  last_ping_at?: string;
  ip_address?: string;
  turnstile_id?: number;
  turnstile_name?: string;
}

export interface ScanData {
  assistant_uuid?: string;
  assistant_dni?: string;
  assistant_name?: string;
  event_id?: number;
  event_name?: string;
  product_name?: string;
  status?: string;
  message?: string;
  allowed?: boolean;
  automatic?: boolean;
  timestamp?: string;
  // Estructura alternativa del backend
  qr_code?: string;
  uuid?: string;
  dni?: string;
  assistant?: {
    name?: string;
    ticket?: string;
  };
}

export interface TurnstileEvent {
  type: TurnstileEventType;
  timestamp: Date;
  turnstile: TurnstileData;
  scanData?: ScanData;
  rawData?: any;
}


export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';


@Injectable({
  providedIn: 'root'
})
export class TurnstileWebSocketService {
  private pusher: Pusher | null = null;
  private channels: Map<number, Channel> = new Map(); // Mapa de eventId -> Channel
  private subscribedEventIds: number[] = []; // IDs de eventos a los que estamos suscritos
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>('disconnected');
  private eventsSubject = new Subject<TurnstileEvent>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 3000; // 3 segundos
  private reconnectTimer: any = null;
  private connectionTimeout: any = null;
  private readonly CONNECTION_TIMEOUT = 10000; // 10 segundos
  private isManualDisconnect = false; 
  private maxReconnectReached = false;
  private shouldProcessErrors = true;

  public readonly connectionStatus$: Observable<ConnectionStatus> =
    this.connectionStatusSubject.asObservable();

  public readonly events$: Observable<TurnstileEvent> =
    this.eventsSubject.asObservable();

  constructor(
    private authService: AuthService,
    private toastService: CustomToastService
  ) {
    console.log('[TurnstileWebSocket] Servicio inicializado');
  }


  public async connect(eventIds?: number[]): Promise<void> {
    if (this.pusher && this.pusher.connection.state === 'connected') {
      console.log('[TurnstileWebSocket] Ya está conectado');
      if (eventIds && eventIds.length > 0) {
        this.subscribedEventIds = eventIds;
        this.subscribeToEventChannels(eventIds);
      }
      return;
    }

    if (this.connectionStatusSubject.value === 'connecting') {
      console.log('[TurnstileWebSocket] Conexión ya en progreso');
      return;
    }

    console.log('[TurnstileWebSocket] Iniciando conexión...');
    this.connectionStatusSubject.next('connecting');
    this.reconnectAttempts = 0;
    this.isManualDisconnect = false;
    this.maxReconnectReached = false;
    this.shouldProcessErrors = true;

    if (eventIds && eventIds.length > 0) {
      this.subscribedEventIds = eventIds;
    }

    try {
      const authData = await this.authService.getLogin();
      if (!authData?.access_token) {
        throw new Error('No hay token de autenticación disponible');
      }

      const wsConfig = environment.websocket;
      const wsHost = wsConfig.host;
      const wsPort = wsConfig.port;
      const wsScheme = wsConfig.scheme;

      let apiBaseUrl: string;
      if (wsHost.includes('192.168.') || wsHost.includes('localhost') || wsHost.includes('127.0.0.1')) {
        apiBaseUrl = `${wsScheme}://${wsHost}:8000`;
      } else {
        // Producción: usar el api_server del environment
        apiBaseUrl = environment.api_server;
      }
      const authEndpoint = `${apiBaseUrl}/api/broadcasting/auth`;

      console.log('[TurnstileWebSocket] ===== INICIANDO CONEXIÓN =====');
      console.log('[TurnstileWebSocket] Endpoint de autenticación:', authEndpoint);
      console.log('[TurnstileWebSocket] Eventos a monitorear:', this.subscribedEventIds);

      // Construir URL completa de WebSocket para logging
      const wsUrl = `${wsScheme === 'https' ? 'wss' : 'ws'}://${wsHost}:${wsPort}`;
      // console.log('[TurnstileWebSocket] URL de conexión WebSocket:', wsUrl);
      // console.log('[TurnstileWebSocket] Host resuelto:', wsHost);
      // console.log('[TurnstileWebSocket] Puerto:', wsPort);
      // console.log('[TurnstileWebSocket] Scheme:', wsScheme);
      // console.log('[TurnstileWebSocket] Configuración completa de Pusher:', {
      //   key: wsConfig.key,
      //   wsHost: wsHost,
      //   wsPort: wsPort,
      //   wssPort: wsPort,
      //   scheme: wsScheme,
      //   enabledTransports: wsConfig.scheme === 'https' ? ['wss'] : ['ws'],
      //   forceTLS: wsConfig.forceTLS,
      //   authEndpoint: authEndpoint,
      //   httpHost: wsHost,
      //   httpPort: wsPort,
      //   httpsPort: wsPort
      // });
      
      // Log adicional para debugging
      // console.log('[TurnstileWebSocket] Token de autenticación disponible:', !!authData?.access_token);
      // console.log('[TurnstileWebSocket] Token (primeros 20 chars):', authData?.access_token);

      this.pusher = new Pusher(wsConfig.key, {
        cluster: '', // Soketi no usa cluster, dejar vacío
        wsHost: wsHost,
        wsPort: wsPort,
        wssPort: wsPort,
        enabledTransports: wsConfig.scheme === 'https' ? ['wss'] : ['ws'],
        forceTLS: wsConfig.forceTLS,
        disableStats: true,
        authEndpoint: authEndpoint, // Endpoint de autenticación de Laravel
        auth: {
          headers: {
            'Authorization': `Bearer ${authData.access_token}`,
            'Accept': 'application/json'
          }
        },
        // Configuraciones específicas para soketi
        httpHost: wsHost,
        httpPort: wsPort,
        httpsPort: wsPort
      });

      this.pusher.connection.bind('state_change', (states: { previous: string, current: string }) => {
        if (!this.shouldProcessErrors) return;
        console.log('[TurnstileWebSocket] ===== CAMBIO DE ESTADO =====');
        console.log('[TurnstileWebSocket] Estado anterior:', states.previous);
        console.log('[TurnstileWebSocket] Estado actual:', states.current);
        console.log('[TurnstileWebSocket] Timestamp:', new Date().toISOString());
        
        // Verificar socket_id cuando el estado cambia a 'connected'
        if (states.current === 'connected' && this.pusher && this.pusher.connection) {
          const socketId = this.pusher.connection.socket_id;
          // console.log('[TurnstileWebSocket] Socket ID en state_change (connected):', socketId);
          // console.log('[TurnstileWebSocket] Estado completo de conexión:', {
          //   state: this.pusher.connection.state,
          //   socket_id: socketId,
          //   activityTimeout: this.pusher.connection.activityTimeout
          // });
        }
        
        this.handleConnectionStateChange(states.current);
      });

      this.pusher.connection.bind('error', (error: any) => {
        if (!this.shouldProcessErrors || this.maxReconnectReached) return;
        console.error('[TurnstileWebSocket] ===== ERROR DE CONEXIÓN =====');
        console.error('[TurnstileWebSocket] Error completo:', error);
        console.error('[TurnstileWebSocket] Tipo de error:', typeof error);
        console.error('[TurnstileWebSocket] Mensaje:', error?.message || error?.error?.message || 'Sin mensaje');
        console.error('[TurnstileWebSocket] Código:', error?.code || error?.error?.code || 'Sin código');
        console.error('[TurnstileWebSocket] Estado actual:', this.pusher?.connection?.state || 'Desconocido');
        console.error('[TurnstileWebSocket] Timestamp:', new Date().toISOString());
        if (error?.data) {
          console.error('[TurnstileWebSocket] Datos del error:', error.data);
        }
        this.handleConnectionError(error);
      });

      this.pusher.connection.bind('connected', () => {
        if (!this.shouldProcessErrors) return;
        console.log('[TurnstileWebSocket] ===== CONECTADO EXITOSAMENTE =====');
        console.log('[TurnstileWebSocket] Timestamp:', new Date().toISOString());
        // Verificar si tenemos socket_id disponible
        if (this.pusher && this.pusher.connection) {
          const socketId = this.pusher.connection.socket_id;
          // console.log('[TurnstileWebSocket] Socket ID obtenido:', socketId);
          console.log('[TurnstileWebSocket] Estado de conexión:', this.pusher.connection.state);
          // console.log('[TurnstileWebSocket] Información completa de conexión:', {
          //   state: this.pusher.connection.state,
          //   socket_id: socketId,
          //   activityTimeout: this.pusher.connection.activityTimeout
          // });
        } else {
          console.warn('[TurnstileWebSocket] Pusher o connection no disponible para obtener socket_id');
        }
        this.handleConnected();
        
        // Suscribirse a los canales DESPUÉS de que la conexión esté establecida
        if (this.subscribedEventIds.length > 0) {
          console.log('[TurnstileWebSocket] Conexión establecida, suscribiendo a canales...');
          this.subscribeToEventChannels(this.subscribedEventIds);
        }
      });

      this.pusher.connection.bind('disconnected', () => {
        if (!this.shouldProcessErrors) return;
        console.warn('[TurnstileWebSocket] ===== DESCONECTADO =====');
        console.warn('[TurnstileWebSocket] Timestamp:', new Date().toISOString());
        console.warn('[TurnstileWebSocket] Estado antes de desconexión:', this.pusher?.connection?.state || 'Desconocido');
        console.warn('[TurnstileWebSocket] Es desconexión manual:', this.isManualDisconnect);
        this.handleDisconnected();
      });

      this.pusher.connection.bind('unavailable', () => {
        console.error('[TurnstileWebSocket] ===== SERVICIO NO DISPONIBLE =====');
        console.error('[TurnstileWebSocket] El servidor WebSocket no está disponible');
        console.error('[TurnstileWebSocket] Timestamp:', new Date().toISOString());
        this.handleConnectionError(new Error('Servicio WebSocket no disponible'));
      });

      this.pusher.connection.bind('failed', () => {
        console.error('[TurnstileWebSocket] ===== CONEXIÓN FALLIDA =====');
        console.error('[TurnstileWebSocket] La conexión falló después de varios intentos');
        console.error('[TurnstileWebSocket] Timestamp:', new Date().toISOString());
        this.handleConnectionError(new Error('Conexión fallida'));
      });

      // NO suscribirse aquí - esperar a que la conexión esté establecida
      // La suscripción se hará en el evento 'connected'
      if (this.subscribedEventIds.length > 0) {
        console.log('[TurnstileWebSocket] Eventos a suscribir después de conexión:', this.subscribedEventIds);
      } else {
        console.warn('[TurnstileWebSocket] No se proporcionaron eventIds, no se suscribirá a ningún canal');
      }

      this.connectionTimeout = setTimeout(() => {
        if (this.connectionStatusSubject.value === 'connecting') {
          console.error('[TurnstileWebSocket] Timeout de conexión');
          
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
          }
          
          if (this.reconnectAttempts >= this.maxReconnectAttempts - 1) {
            this.reconnectAttempts = this.maxReconnectAttempts;
            this.maxReconnectReached = true;
            this.shouldProcessErrors = false;
            this.connectionStatusSubject.next('error');
            
            const message = 'Parece que perdimos la conexión con el servidor. Por favor, verifica tu conexión a internet e intenta nuevamente más tarde.';
            this.toastService.show(message, 8000, 'error');
            return;
          }
          
          this.handleConnectionError(new Error('Timeout de conexión'));
        }
      }, this.CONNECTION_TIMEOUT);

    } catch (error) {
      console.error('[TurnstileWebSocket] Error al inicializar conexión:', error);
      this.handleConnectionError(error);
    }
  }

  private subscribeToEventChannels(eventIds: number[]): void {
    if (!this.pusher) {
      console.error('[TurnstileWebSocket] Pusher no está inicializado');
      return;
    }

    console.log('[TurnstileWebSocket] Suscribiendo a canales privados para eventos:', eventIds);

    eventIds.forEach(eventId => {
      if (this.channels.has(eventId)) {
        console.log(`[TurnstileWebSocket] Ya suscrito al evento ${eventId}`);
        return;
      }

      const channelName = `private-turnstiles.event.${eventId}`;
      // console.log(`[TurnstileWebSocket] Suscribiendo a canal: ${channelName}`);

      if (!this.pusher) {
        console.error('[TurnstileWebSocket] Pusher es null, no se puede suscribir');
        return;
      }

      // Verificar socket_id antes de suscribirse
      if (this.pusher.connection) {
        const socketId = this.pusher.connection.socket_id;
        const connectionState = this.pusher.connection.state;
        // console.log(`[TurnstileWebSocket] Antes de suscribir a ${channelName} - Socket ID:`, socketId, 'Estado:', connectionState);
        
        if (!socketId) {
          console.warn(`[TurnstileWebSocket] ⚠️ Socket ID no disponible antes de suscribirse al canal ${channelName}`);
        }
      } else {
        console.warn('[TurnstileWebSocket] Connection no disponible antes de suscribirse');
      }

      const channel = this.pusher.subscribe(channelName);

      channel.bind('pusher:subscription_succeeded', () => {
        // console.log(`[TurnstileWebSocket] Suscrito exitosamente a canal: ${channelName}`);
        this.bindEventListeners(channel, eventId);
      });

      channel.bind('pusher:subscription_error', (error: any) => {
        // console.error(`[TurnstileWebSocket] Error al suscribirse a ${channelName}:`, error);
        if (error.status === 403 || error.status === 401) {
          console.error(`[TurnstileWebSocket] Acceso denegado al evento ${eventId}. Verifica los permisos del usuario.`);
        }
        this.channels.delete(eventId);
      });

      this.channels.set(eventId, channel);
    });
  }

  private bindEventListeners(channel: Channel, eventId: number): void {
    if (!channel) {
      console.error('[TurnstileWebSocket] No hay canal disponible para bind');
      return;
    }

    console.log(`[TurnstileWebSocket] Configurando listeners para evento ${eventId}...`);

    channel.bind('turnstile.connected', (data: any) => {
      console.log(`🟢 [TurnstileWebSocket] EVENTO RECIBIDO: turnstile.connected (evento ${eventId})`, data);
      this.handleEvent('turnstile.connected', data);
    });

    channel.bind('turnstile.disconnected', (data: any) => {
      console.log(`🔴 [TurnstileWebSocket] EVENTO RECIBIDO: turnstile.disconnected (evento ${eventId})`, data);
      this.handleEvent('turnstile.disconnected', data);
    });

    channel.bind('turnstile.scanned', (data: any) => {
      console.log(`[TurnstileWebSocket] EVENTO RECIBIDO: turnstile.scanned (evento ${eventId})`, data);
      this.handleEvent('turnstile.scanned', data);
    });

    console.log(`[TurnstileWebSocket] Listeners configurados correctamente para evento ${eventId}`);
  }


  private handleEvent(type: TurnstileEventType, data: any): void {
    console.log('[TurnstileWebSocket] Procesando evento:', type, 'con datos:', data);

    let turnstile: TurnstileData = {};
    let scanData: ScanData | undefined = undefined;

    if (type === 'turnstile.connected' || type === 'turnstile.disconnected') {
      turnstile = {
        id: data.id,
        name: data.name,
        mac_address: data.mac_address,
        is_online: data.is_online,
        last_ping_at: data.last_ping_at,
        ip_address: data.ip_address,
        location: data.location,
        last_scan_at: data.last_scan_at
      };
    } else if (type === 'turnstile.scanned') {
      turnstile = {
        id: data.turnstile_id || data.turnstile?.id || data.id,
        name: data.turnstile_name || data.turnstile?.name || data.name,
        mac_address: data.mac_address || data.turnstile?.mac_address,
        is_online: data.turnstile?.is_online ?? data.is_online,
        location: data.turnstile?.location || data.location
      };

      if (data.scan) {
        scanData = {
          uuid: data.scan.uuid,
          assistant_uuid: data.scan.uuid,
          dni: data.scan.dni,
          assistant_dni: data.scan.dni,
          assistant_name: data.scan.assistant?.fullname,
          event_id: data.scan.event_id,
          allowed: data.scan.allowed,
          message: data.scan.message,
          timestamp: data.timestamp,
          qr_code: data.scan.qr_code,
          assistant: data.scan.assistant
        };
      } else if (data.scanData) {
        scanData = data.scanData;
      } else if (data.message || data.status) {
        scanData = {
          allowed: data.allow !== undefined ? data.allow : (data.allowed !== undefined ? data.allowed : false),
          message: data.message,
          status: data.status?.toString(),
          timestamp: data.timestamp,
          event_id: data.event_id
        };
      }
    } else {
      turnstile = data.turnstile || {
        id: data.id || data.turnstile_id,
        name: data.name || data.turnstile_name,
        mac_address: data.mac_address
      };
      scanData = data.scanData || (data.scan ? {
        uuid: data.scan.uuid,
        assistant_uuid: data.scan.uuid,
        dni: data.scan.dni,
        assistant_dni: data.scan.dni,
        assistant_name: data.scan.assistant?.fullname,
        event_id: data.scan.event_id,
        allowed: data.scan.allowed,
        message: data.scan.message,
        timestamp: data.timestamp
      } : undefined);
    }

    const event: TurnstileEvent = {
      type,
      timestamp: new Date(),
      turnstile,
      scanData,
      rawData: data
    };

    console.log('[TurnstileWebSocket] Evento procesado:', event);
    this.eventsSubject.next(event);
  }

  private handleConnectionStateChange(state: string): void {
    switch (state) {
      case 'connected':
        this.handleConnected();
        break;
      case 'disconnected':
        this.handleDisconnected();
        break;
      case 'failed':
      case 'unavailable':
        this.handleConnectionError(new Error(`Estado de conexión: ${state}`));
        break;
      default:
        break;
    }
  }


  private handleConnected(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    // Verificar socket_id nuevamente en handleConnected
    if (this.pusher && this.pusher.connection) {
      const socketId = this.pusher.connection.socket_id;
      // console.log('[TurnstileWebSocket] Socket ID en handleConnected:', socketId);
    }

    this.connectionStatusSubject.next('connected');
    this.reconnectAttempts = 0;
    this.maxReconnectReached = false;
    this.shouldProcessErrors = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }


  private handleDisconnected(): void {
    if (!this.shouldProcessErrors || this.maxReconnectReached) return;
    
    if (this.connectionStatusSubject.value !== 'disconnected') {
      this.connectionStatusSubject.next('disconnected');
    }

    if (!this.isManualDisconnect) {
      this.attemptReconnect();
    } else {
      console.log('[TurnstileWebSocket] Desconexión manual detectada, no se intentará reconectar automáticamente');
    }
  }


  private handleConnectionError(error: any): void {
    if (!this.shouldProcessErrors || this.maxReconnectReached) return;
    
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    this.connectionStatusSubject.next('error');

    if (!this.isManualDisconnect) {
      this.attemptReconnect();
    } else {
      console.log('[TurnstileWebSocket] Error después de desconexión manual, no se intentará reconectar');
    }
  }

  private attemptReconnect(): void {
    if (this.isManualDisconnect) {
      console.log('[TurnstileWebSocket] Desconexión manual activa, cancelando reconexión automática');
      return;
    }

    if (this.maxReconnectReached) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[TurnstileWebSocket] Máximo de intentos de reconexión alcanzado');
      this.maxReconnectReached = true;
      this.shouldProcessErrors = false;
      this.connectionStatusSubject.next('error');
      
      const message = 'Parece que perdimos la conexión con el servidor. Por favor, verifica tu conexión a internet e intenta nuevamente más tarde.';
      this.toastService.show(message, 8000, 'error');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    console.log(`[TurnstileWebSocket] Intentando reconectar en ${delay}ms (intento ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(async () => {
      if (this.isManualDisconnect || this.maxReconnectReached) {
        return;
      }
      console.log('[TurnstileWebSocket] Reconectando...');
      this.disconnect(false);
      await this.connect(this.subscribedEventIds);
    }, delay);
  }

  public disconnect(manual: boolean = true): void {
    console.log(`[TurnstileWebSocket] Desconectando${manual ? ' manualmente' : ''}...`);
    
    if (manual) {
      this.isManualDisconnect = true;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    this.channels.forEach((channel, eventId) => {
      console.log(`[TurnstileWebSocket] Desuscribiéndose del canal del evento ${eventId}`);
      channel.unbind_all();
      if (this.pusher) {
        this.pusher.unsubscribe(`private-turnstiles.event.${eventId}`);
      }
    });
    this.channels.clear();

    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }

    this.connectionStatusSubject.next('disconnected');
    this.reconnectAttempts = 0;
    this.subscribedEventIds = [];
  }


  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  public cleanup(): void {
    this.disconnect();
    this.eventsSubject.complete();
    this.connectionStatusSubject.complete();
  }
}

